import React from 'react';
import {useDispatch, useRuntimeEnvironment} from '@packages/systems/render';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {weakMemo} from '@packages/utilities/memo';
import {
  SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
  SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
} from '@packages/systems/core/constants/SharedConstraintRules';
import {
  DYN_LIST_RENDERED_TOTAL_PAGES_UPDATED,
  DYN_NODE_ITEM_COUNT_UPDATED,
} from '@packages/systems/designer/actions';

import {
  CLASS_NAME_DYNAMIC_WRAPPER,
  CLASS_NAME_DYNAMIC_LIST_REPEATER_REF,
} from './constants';
import {
  TListOfElement,
  TTypeConstructor,
  TTypeReference,
  TRecord,
  TNumber,
  TText,
  TList,
  TUnion,
  TEmptyRecord,
  TBoolean,
} from '@packages/systems/wfdl-ast';
import {
  type ContextType,
  Atom,
  Plugins,
  withMeta,
  withSystem,
  AtomRenderProps,
} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {
  iconName,
  qualify,
  pluginName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {isSetFieldDynamoWrapperElement} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {isDynamoWrapper} from '@packages/utilities/elementTypeUtils';
import {
  getChildren,
  getInPojo,
  getTagOrElse,
} from '@packages/utilities/data-utils';

const setContextInsideWrapperTrue = weakMemo((context: ContextType) =>
  context.set('insideWrapper', true)
);

type Props = {
  dispatch: (action: {type: string; payload: unknown}) => void;
  elementId: AtomRenderProps['elementId'];
  data: AtomRenderProps['data'];
  nativeIdPath: AtomRenderProps['nativeIdPath'];
  attributes: AtomRenderProps['attributes'];
  classes: AtomRenderProps['classes'];
  generateAttributes: AtomRenderProps['generateAttributes'];
  renderChild: AtomRenderProps['renderChild'];
  runtimeEnvironment: Environment;
  context: AtomRenderProps['context'];
};

class DynamoWrapper extends React.Component<Props> {
  shouldComponentUpdate(nextProps: Props) {
    return !nextProps.context.get('loading');
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.runtimeEnvironment !== WebflowEnvironments.DESIGNER) {
      return;
    }

    if (
      // if the paginateable number of items has changed
      prevProps.context.get('totalItems') !==
      this.props.context.get('totalItems')
    ) {
      this.props.dispatch({
        type: DYN_NODE_ITEM_COUNT_UPDATED,
        payload: {
          nodeId: this.props.elementId,
          totalItems: this.props.context.get('totalItems'),
        },
      });
    }

    const nextTotalPages = this.props.context.get('totalPages');
    if (
      typeof nextTotalPages === 'number' &&
      nextTotalPages !== prevProps.context.get('totalPages')
    ) {
      this.props.dispatch({
        type: DYN_LIST_RENDERED_TOTAL_PAGES_UPDATED,
        payload: {
          wrapperNativeIdPath: [
            ...this.props.nativeIdPath,
            this.props.elementId,
          ],
          totalPages: nextTotalPages,
        },
      });
    }
  }

  render() {
    const {
      data,
      attributes,
      classes,
      generateAttributes,
      renderChild,
      context,
    } = this.props;

    const Tag = getTagOrElse(getInPojo(data, ['tag']), 'div');

    const updatedContext = setContextInsideWrapperTrue(context);
    const repeaterSlugPath = context.get('repeaterSlugPath');

    const wrapperClasses = classes
      .push(CLASS_NAME_DYNAMIC_WRAPPER)
      .concat(
        repeaterSlugPath && repeaterSlugPath.length > 1
          ? CLASS_NAME_DYNAMIC_LIST_REPEATER_REF
          : []
      );

    return (
      <Tag {...generateAttributes(attributes, wrapperClasses)}>
        {context.get('loading')
          ? null
          : getChildren(data).map((child) =>
              renderChild(child, updatedContext)
            )}
      </Tag>
    );
  }
}

const GlobalDefaultWithLocalFallback = withSystem(
  {private: true},
  TUnion({
    GlobalDefault: TEmptyRecord,
    True: TEmptyRecord,
    False: TEmptyRecord,
  })
);

const config = createIntegratedAtom({
  displayName: 'Collection List Wrapper',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Dynamo'), iconName('Collection')),
  },
  uiStateOnSelected: 'items',
  showEmpty: true,
  render: Atom.createRender((props) => {
    const dispatch = useDispatch();
    const runtimeEnvironment = useRuntimeEnvironment();
    return (
      <DynamoWrapper
        dispatch={dispatch}
        elementId={props.elementId}
        data={props.data}
        nativeIdPath={props.nativeIdPath}
        attributes={props.attributes}
        classes={props.classes}
        generateAttributes={props.generateAttributes}
        renderChild={props.renderChild}
        runtimeEnvironment={runtimeEnvironment}
        context={props.context}
      />
    );
  }),
  constraints: {
    // to enable nested collection lists, allow DynamoWrapper descendants
    descendants: SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS.filter(
      (constraint) => constraint.is !== 'DynamoWrapper'
    ),
    // to restrict nested collection lists, forbid nesting more than two levels
    ancestors: [
      {is: 'DynamoWrapper', rule: 'AtMostN', maxCount: 2},
      {
        is: {
          // prevent nesting a collection list inside another
          // collection list that is bound to a multi-image field
          check: (element) =>
            isDynamoWrapper(element) && isSetFieldDynamoWrapperElement(element),
          name: 'Multi-image list',
        },
        rule: 'Forbid',
      },
      ...SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS.filter(
        (constraint) => constraint.is !== 'DynamoWrapper'
      ),
    ],
    children: [
      {
        is: ['DynamoList', 'DynamoEmpty', 'Pagination'],
        name: 'Element',
        rule: 'RequireOnly',
      },
    ],
  },
  fields: {
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    // Private query Binding sites for Collection List filter/sort/curated/
    // usePrimaryLocale. Nested EE writes walk up via ElementLocalizationUtils.
    // Keep dyn open via dynExtraFields so type/collection/bind still typecheck.
    dyn: withSystem(
      {private: true},
      TRecord(
        {
          query: withSystem(
            {private: true},
            TRecord(
              {
                usePrimaryLocale: withSystem(
                  {private: true, isLocalizable: true, defaultValue: false},
                  TBoolean
                ),
                filter: withSystem(
                  {private: true, isLocalizable: true},
                  TRecord(
                    {
                      filterFor: withSystem({defaultValue: 'ALL'}, TText),
                      fields: TList(
                        TRecord(
                          {
                            fieldPath: TText,
                            operatorName: TText,
                          },
                          // `value` omitted: live JSON is Text | Number |
                          // List[Text]. Undeclared keeps reify naive. Unique
                          // Filter EE types value as bindable TText.
                          TTypeReference('dynQueryFilterFieldExtraFields')
                        )
                      ),
                    },
                    TTypeReference('dynQueryFilterExtraFields')
                  )
                ),
                sort: withSystem(
                  {private: true, isLocalizable: true},
                  TList(
                    TRecord(
                      {
                        field: TText,
                        direction: TText,
                      },
                      TTypeReference('dynQuerySortItemExtraFields')
                    )
                  )
                ),
                curated: withSystem(
                  {private: true, isLocalizable: true},
                  TRecord(
                    {
                      itemIds: TList(TText),
                    },
                    TTypeReference('dynQueryCuratedExtraFields')
                  )
                ),
              },
              TTypeReference('dynQueryExtraFields')
            )
          ),
        },
        TTypeReference('dynExtraFields')
      )
    ),
    tag: withMeta(
      {variant: 'menu'},
      withSystem(
        {
          defaultValue: 'div',
          label: 'Tag',
          private: true,
        },
        TTypeConstructor(qualify(pluginName('Basic'), typeName('TagsDropdown')))
      )
    ),
    temp: TRecord({
      collectionListItemsPreviewEnabled: GlobalDefaultWithLocalFallback,
      collectionListItemsPreviewCount: withSystem(
        {
          private: true,
          defaultValue: 3,
        },
        TNumber
      ),
    }),
    children: TListOfElement,
  },
});

export default config;
